import React, {useState, useEffect, useRef, useContext, useMemo } from 'react';
import { paymentStatuses } from '../../constants/paymentStatuses';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import useStyles from './styles';
import { CurrencyFormats, zeroDecimalCurrency, PaymentModes } from '../../utils/index';
import axios from "../../api/axios";
import {ProviderTypeContext} from "../../containers/InitCheckoutContainer";
import WeChatPayResult from '../Apms/WechatPay/result';
import GiropayResult from '../Apms/GiroPay/result';
import Przelewy24Result from '../Apms/Przelewy24/result';
import IDealResult from '../Apms/IDeal/result';
import SofortResult from '../Apms/Sofort/result';
import TamaraResult from '../Apms/Tamara/result';
import BancontactResult from '../Apms/Bancontact/result';
import MultibancoResult from '../Apms/Multibanco/result';
import EpsResult from '../Apms/Eps/result';
import AlipayCNResult from '../Apms/AlipayCN/result';
import PostFinanceResult from '../Apms/PostFinance/result';
import PayPalResult from '../Apms/PayPal/result';
import {ApmsTypes} from "../../constants";

function RequiresActionModal({ paymentInfo, onPaymentIntentSucceeded, onError, encryptedPubKey, withSideBar }) {
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [isFrame, setIsFrame] = useState(false);
    const [isDiv, setIsDiv] = useState(false);
    const {providerType} = useContext(ProviderTypeContext);
    const intervalRef = useRef();
    const [divSource, setDivSource] = useState('');
    const [divBrand, setDivBrand] = useState('');
    const fullSizeBrands = useMemo(() => [ApmsTypes.Przelewy24, ApmsTypes.IDeal, ApmsTypes.Giropay, ApmsTypes.Sofort,
        ApmsTypes.Tamara, ApmsTypes.Bancontact, ApmsTypes.Multibanco, ApmsTypes.EPS, ApmsTypes.AlipayCN, ApmsTypes.PostFinance]);
    const isEmbedded = window.self !== window.top;

    useEffect(() => {
        if (paymentInfo
            && paymentInfo.status === paymentStatuses.RequiresAction
            && !!paymentInfo.confirmation3dsLink
        ) {
            setIsFrame(true);
            setOpen(true);
            if (!intervalRef.current) {
                checkPayment(1500);
            }
        } else if (paymentInfo
            && paymentInfo.status === paymentStatuses.RequiresAction
            && paymentInfo.metadata3ds) {
                handleApm();
        }
    }, [paymentInfo]);

    const checkPayment = (interval) => {
        intervalRef.current = setInterval(retrievePaymentIntent, interval);
    }

    const handleApm = async () => {
        if (!paymentInfo.metadata3ds)
            return;

        const {brand, link} = paymentInfo.metadata3ds;
        if (!link)
            return;

        setDivSource(link);
        setDivBrand(brand);
        setIsDiv(true);

        setOpen(true);
        if (!intervalRef.current) {
            checkPayment(4000);
        }
    }

    const retrievePaymentIntent = async () => {
        const { data } = await axios.get(`/payment/check-status?pubKey=${encryptedPubKey}`);
        const isAuthOnly = paymentInfo?.type === PaymentModes.Authorize;
        const closeStatuses = ['Succeeded', 'canceled', 'Canceled', 'requires_payment_method', 'RequiresPaymentMethod'];
        if (isAuthOnly) {
            closeStatuses.push('RequiresCapture');
        }

        if (closeStatuses.includes(data.status)) {
            handleClose();
        }

        if (data.status === 'RequiresPaymentMethod' || data.status === 'canceled' || data.status === 'Canceled') {
            onError();
            return;
        }

        if (data.status === 'Succeeded' || (isAuthOnly && data.status === 'RequiresCapture')) {
            onPaymentIntentSucceeded();
        }
    }

    const handleClose = () => {
        setOpen(false);
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }

    const getAmount = () => {
        const precision = zeroDecimalCurrency({ decimals: paymentInfo?.currencyDecimals }) ? 0 : 2;
        return CurrencyFormats(paymentInfo?.paymentIntentAmount, precision);
    }

    const isFullSize = () => fullSizeBrands.includes(divBrand);

    const resultForm = useMemo(() => {
        switch (divBrand) {
            case ApmsTypes.WeChatPay:
                return <WeChatPayResult link={divSource} />
            case ApmsTypes.Giropay:
                return <GiropayResult link={divSource} />
            case ApmsTypes.Przelewy24:
                return <Przelewy24Result link={divSource} />
            case ApmsTypes.IDeal:
                return <IDealResult link={divSource} />
            case ApmsTypes.Sofort:
                return <SofortResult link={divSource} />
            case ApmsTypes.Tamara:
                return <TamaraResult link={divSource} />
            case ApmsTypes.Bancontact:
                return <BancontactResult link={divSource} />
            case ApmsTypes.Multibanco:
                return <MultibancoResult link={divSource} />
            case ApmsTypes.EPS:
                return <EpsResult link={divSource} />
            case ApmsTypes.AlipayCN:
                return <AlipayCNResult link={divSource} />
            case ApmsTypes.PostFinance:
                return <PostFinanceResult link={divSource} />
            case ApmsTypes.PayPal:
                return <PayPalResult link={divSource} />
            default:
                return null;
        }
    }, [divBrand, isDiv]);

    if (!paymentInfo) {
        return <></>
    }

    const getDialogClasses = () => {
        let style = classes.valid_modal;
        if (isFrame) {
            style += ` ${classes.valid_modal_frame}`;
        }
        if ((withSideBar || isFrame)) {
            style += ` ${classes.valid_modal_withSideBar}`;
        }
        if (isFullSize()) {
            style += ` ${classes.fullModalSize}`;
        }

        if(isEmbedded) {
            style += ` ${classes.valid_modal_withSideBarEmbeded}`;
        }

        return style;
    }

    const getLeftSidebarClasses = () => {
        let style = classes.valid_modal_inner_left;

        if (isFullSize()) {
            style += ` ${classes.fullModal_modal_inner_left}`;
        }

        return style;
    }

    const getRightSidebarClasses = () => {
        let style = classes.valid_modal_inner_right;

        if (isFullSize()) {
            style += ` ${classes.fullModal_modal_inner_right}`;
        }

        return style;
    }

    return (
        <div>
            {
                open && <div className={classes.valid_modal_background}></div>
            }
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogContent>
                    <div className={getDialogClasses()}>
                        <div className={classes.valid_modal_inner}>
                            {withSideBar && (
                                <div className={getLeftSidebarClasses()}>
                                    <h2>Payment method</h2>
                                    <table width="100%" style={{width: '100%'}}>
                                        <tbody>
                                        <tr>
                                            <td>Merchant</td>
                                            <td style={{textAlign: 'right'}} align="right">{paymentInfo.companyName}</td>
                                        </tr>
                                        <tr>
                                            <td>Amount</td>
                                            <td style={{textAlign: 'right'}} align="right">{paymentInfo.currency} {getAmount()}</td>
                                        </tr>
                                        <tr>
                                            <td>Date</td>
                                            <td style={{textAlign: 'right'}} align="right">{paymentInfo.paymentProcessedDate}</td>
                                        </tr>
                                        {
                                            !isDiv && (
                                                <tr>
                                                    <td>Card number</td>
                                                    <td style={{ textAlign: 'right' }} align="right">**** **** **** {paymentInfo.paymentCardLast4}</td>
                                                </tr>
                                            )
                                        }
                                        </tbody>
                                    </table>
                                    <button className={classes.changeCard} onClick={handleClose}>Use another card</button>
                                </div>
                            )}
                            {
                                isFrame && (
                                    <div key="iFrame-div" className={getRightSidebarClasses()}>
                                        <iframe
                                            id="3ds-frame"
                                            className={classes.threeDsFrame}
                                            src={paymentInfo.confirmation3dsLink}
                                            title='3ds link'
                                            frameBorder="0"
                                            scrolling="no"
                                            aria-hidden="true"
                                        ></iframe>
                                    </div>
                                )
                            }
                            {
                                isDiv && (
                                    <div key="iFrame-div" className={getRightSidebarClasses()}>
                                        {resultForm}
                                    </div>
                                )
                            }
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    )
}

export default RequiresActionModal;
